<script setup>
import { reactive, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useInboxSignatures } from 'dashboard/composables/useInboxSignatures';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  isLikelyPhoneInput,
  processContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import DelfosPhoneRecipientsAPI from 'dashboard/api/delfosPhoneRecipients';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { pendingGroupNavigation } from 'dashboard/helper/pendingGroupNavigation';

import Popover from 'dashboard/components-next/popover/Popover.vue';
import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';
import ComposeNewGroupForm from 'dashboard/components-next/NewConversation/components/ComposeNewGroupForm.vue';

const props = defineProps({
  contactId: {
    type: String,
    default: null,
  },
  align: {
    type: String,
    default: 'end',
  },
  initialPhone: {
    type: String,
    default: '',
  },
  initialName: {
    type: String,
    default: '',
  },
  initialInboxId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close']);

const searchContacts = createContactSearcher();
const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const popoverRef = ref(null);
const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isResolvingRecipient = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const composeMode = ref('conversation');
const groupFormRef = ref(null);

const DELFOS_PHONE_INBOX_IDS = new Set([1, 3]);

const formState = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const clearFormState = () => {
  Object.assign(formState, {
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
};

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');
const groupUiFlags = useMapGetter('groupMembers/getUIFlags');

const groupCreationInboxes = computed(() =>
  inboxesList.value.filter(inbox => inbox.allow_group_creation)
);

const isGroupMode = computed(() => composeMode.value === 'group');
const hasGroupInboxes = computed(() => groupCreationInboxes.value.length > 0);
const isGroupsDisabled = computed(
  () => !globalConfig.value.baileysWhatsappGroupsEnabled
);
const isSuperAdmin = computed(() => currentUser.value.type === 'SuperAdmin');

const {
  fetchInboxSignatures,
  getSignatureForInbox,
  getSignatureSettingsForInbox,
} = useInboxSignatures();

fetchInboxSignatures();

const resolvedMessageSignature = computed(() => {
  if (!targetInbox.value?.id) return messageSignature.value;
  return getSignatureForInbox(targetInbox.value.id);
});

const resolvedSignatureSettings = computed(() => {
  if (!targetInbox.value?.id) return null;
  return getSignatureSettingsForInbox(targetInbox.value.id);
});

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const resetContacts = () => {
  contacts.value = [];
};

const buildPendingPhoneContact = (phone, name = '') => {
  const contactableInboxes = inboxesList.value
    .filter(inbox => DELFOS_PHONE_INBOX_IDS.has(Number(inbox.id)))
    .map(inbox => ({ id: inbox.id, sourceId: null }));
  const pendingName = name.trim();

  return {
    id: null,
    name: pendingName || phone.trim(),
    phoneNumber: phone.trim(),
    pendingPhone: phone.trim(),
    pendingName,
    contactInboxes: mergeInboxDetails(contactableInboxes, inboxesList.value),
  };
};

const preferredInitialInbox = contact => {
  const inboxId = Number(props.initialInboxId);
  if (!inboxId) return null;

  return (
    contact.contactInboxes.find(inbox => Number(inbox.id) === inboxId) || null
  );
};

const prepareInitialPhone = () => {
  const phone = props.initialPhone?.trim();
  if (!phone || !isLikelyPhoneInput(phone)) return;
  if (selectedContact.value?.pendingPhone === phone) return;

  const contact = buildPendingPhoneContact(phone, props.initialName);
  if (!contact.contactInboxes.length) return;
  selectedContact.value = contact;
  targetInbox.value = preferredInitialInbox(contact);
  contacts.value = [];
};

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      const results = await searchContacts(query);
      // null means the request was aborted (a newer search is in-flight),
      if (results === null) return;
      contacts.value = results;
      isSearching.value = false;
    } catch (error) {
      isSearching.value = false;
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    }
  },
  400,
  false
);

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    if (isLikelyPhoneInput(value)) {
      contact = buildPendingPhoneContact(value);
      if (!contact.contactInboxes.length) {
        useAlert('Nenhum canal de WhatsApp disponivel para este usuario.');
        return;
      }
      selectedContact.value = contact;
      contacts.value = [];
      return;
    }

    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  contacts.value = [];
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      // Merge the processed contactableInboxes with the inboxesList
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );

      isFetchingInboxes.value = false;
    } catch (error) {
      isFetchingInboxes.value = false;
    }
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  if (!inbox) clearFormState();
  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
  clearFormState();
};

const closeCompose = () => {
  popoverRef.value?.hide();
};

const discardCompose = () => {
  clearFormState();
  formState.message = '';
  closeCompose();
};

const switchMode = mode => {
  if (composeMode.value === mode) return;
  composeMode.value = mode;
  selectedContact.value = null;
  targetInbox.value = null;
  clearFormState();
  formState.message = '';
  resetContacts();
  groupFormRef.value?.resetForm();
};

const resolvePendingPhoneRecipient = async payload => {
  const pendingPhone = selectedContact.value?.pendingPhone;
  if (!pendingPhone) return payload;

  if (!targetInbox.value?.id) {
    throw new ExceptionWithMessage('Escolha o WhatsApp de envio.');
  }

  isResolvingRecipient.value = true;
  try {
    const { data } = await DelfosPhoneRecipientsAPI.resolve({
      phone: pendingPhone,
      name: selectedContact.value?.pendingName,
      inboxId: targetInbox.value.id,
      createIfMissing: true,
    });
    const contact = data?.contact;
    const sourceId = data?.contact_inbox?.source_id;
    if (!contact?.id || !sourceId) {
      throw new ExceptionWithMessage(
        'Nao foi possivel preparar o contato para envio.'
      );
    }

    selectedContact.value = {
      id: contact.id,
      name: contact.name,
      phoneNumber: contact.phone_number,
      pendingPhone: null,
      pendingName: null,
    };
    targetInbox.value = { ...targetInbox.value, sourceId };

    return {
      ...payload,
      contactId: Number(contact.id),
      inboxId: Number(targetInbox.value.id),
      sourceId,
    };
  } catch (error) {
    if (error instanceof ExceptionWithMessage) throw error;

    throw new ExceptionWithMessage(
      error?.response?.data?.message ||
        'Nao foi possivel confirmar este numero no WhatsApp.'
    );
  } finally {
    isResolvingRecipient.value = false;
  }
};

const createGroup = async ({ inboxId, subject, participants }) => {
  try {
    const data = await store.dispatch('groupMembers/createGroup', {
      inbox_id: inboxId,
      subject,
      participants,
    });
    pendingGroupNavigation.set(data.group_jid);
    groupFormRef.value?.resetForm();
    discardCompose();
    useAlert(t('GROUP.CREATE.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('GROUP.CREATE.ERROR_MESSAGE'));
  }
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    const resolvedPayload = await resolvePendingPhoneRecipient(payload);
    const data = await store.dispatch('contactConversations/create', {
      params: resolvedPayload,
      isFromWhatsApp,
    });
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    discardCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true; // Return success
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const onPopoverShow = () => {
  // Flag to prevent triggering drag n drop while compose is open
  prepareInitialPhone();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onPopoverHide = () => {
  composeMode.value = 'conversation';
  if (!props.contactId) {
    selectedContact.value = null;
  }
  targetInbox.value = null;
  resetContacts();
  groupFormRef.value?.resetForm();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
  emit('close');
};

watch(
  activeContact,
  (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      // Reset on contact change
      if (currentContact?.id !== previousContact?.id) {
        clearSelectedContact();
        clearFormState();
        formState.message = '';
      }

      // First process the contactable inboxes to get the right structure
      const processedInboxes = processContactableInboxes(
        currentContact.contactInboxes || []
      );
      // Then Merge processedInboxes with the inboxes list
      selectedContact.value = {
        ...currentContact,
        contactInboxes: mergeInboxDetails(processedInboxes, inboxesList.value),
      };
    }
  },
  { immediate: true, deep: true }
);

const navigateToGroup = ({ conversationId }) => {
  const url = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: conversationId,
    })
  );
  router.push({ path: url });
};

onMounted(() => {
  resetContacts();
  emitter.on(BUS_EVENTS.NAVIGATE_TO_GROUP, navigateToGroup);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.NAVIGATE_TO_GROUP, navigateToGroup);
});
</script>

<template>
  <Popover
    ref="popoverRef"
    :align="align"
    :show-content-border="false"
    @show="onPopoverShow"
    @hide="onPopoverHide"
  >
    <template #default="{ isOpen }">
      <slot name="trigger" :is-open="isOpen" />
    </template>
    <template #content>
      <div class="w-full md:w-[42rem] flex flex-col min-w-0">
        <div
          v-if="hasGroupInboxes"
          class="flex gap-1 px-4 pt-3 pb-0 bg-n-alpha-3 border border-b-0 border-n-strong backdrop-blur-[100px] rounded-t-xl"
        >
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors"
            :class="
              !isGroupMode
                ? 'text-n-brand border-n-brand bg-n-alpha-2'
                : 'text-n-slate-11 border-transparent hover:text-n-slate-12'
            "
            @click="switchMode('conversation')"
          >
            {{ t('COMPOSE_NEW_CONVERSATION.TAB_CONVERSATION') }}
          </button>
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors"
            :class="
              isGroupMode
                ? 'text-n-brand border-n-brand bg-n-alpha-2'
                : 'text-n-slate-11 border-transparent hover:text-n-slate-12'
            "
            @click="switchMode('group')"
          >
            {{ t('COMPOSE_NEW_CONVERSATION.TAB_GROUP') }}
          </button>
        </div>
        <ComposeNewConversationForm
          v-if="!isGroupMode"
          :form-state="formState"
          :class="{ '!rounded-t-none !border-t-0': hasGroupInboxes }"
          :contacts="contacts"
          :contact-id="contactId"
          :is-loading="isSearching"
          :current-user="currentUser"
          :selected-contact="selectedContact"
          :target-inbox="targetInbox"
          :is-creating-contact="isCreatingContact"
          :is-resolving-recipient="isResolvingRecipient"
          :is-fetching-inboxes="isFetchingInboxes"
          :is-direct-uploads-enabled="directUploadsEnabled"
          :contact-conversations-ui-flags="uiFlags"
          :contacts-ui-flags="contactsUiFlags"
          :message-signature="resolvedMessageSignature"
          :send-with-signature="sendWithSignature"
          :signature-settings="resolvedSignatureSettings"
          @search-contacts="onContactSearch"
          @reset-contact-search="resetContacts"
          @update-selected-contact="handleSelectedContact"
          @update-target-inbox="handleTargetInbox"
          @clear-selected-contact="clearSelectedContact"
          @create-conversation="createConversation"
          @discard="discardCompose"
        />
        <ComposeNewGroupForm
          v-else
          ref="groupFormRef"
          class="!rounded-t-none !border-t-0"
          :inboxes="groupCreationInboxes"
          :is-creating="groupUiFlags.isCreating"
          :is-groups-disabled="isGroupsDisabled"
          :is-super-admin="isSuperAdmin"
          @create-group="createGroup"
          @discard="discardCompose"
        />
      </div>
    </template>
  </Popover>
</template>
